/**
 * components/ResponsiveTable.js — ДЭЛГЭЦЭЭ МЭДЭРДЭГ жагсаалт.
 *
 * Өргөн дэлгэцэнд antd `<Table>`-тэй ЯГ адил. Нарийн дэлгэцэнд (утас)
 * ХҮСНЭГТ БИШ, КАРТАН ЖАГСААЛТ болж хувирна.
 *
 * ХЭРЭГЛЭЭ — нэг мөр солино:
 *     <Table …/>            →   <ResponsiveTable …/>
 * Бусад бүх prop (columns, dataSource, pagination, loading, rowKey…)
 * хэвээрээ дамжина.
 *
 * ─────────────────────────────────────────────────────────────────────
 * ⚠ ЯАГААД ИЙМ АРГА ВЭ — «үйлдлийн товчнуудыг яаж харуулах вэ»
 *
 *   Утасны жагсаалтыг ГАРААР бичвэл хуудас бүрд үйлдлийн товч, эрхийн
 *   дүрэм ДАХИН бичигдэнэ. Тэгвэл нэгийг засахад нөгөө нь хоцорч,
 *   утсан дээр эрхгүй хүнд товч гарч 403 буцах эрсдэлтэй. Мөн 59 файлыг
 *   гараар хөрвүүлэх нь бодит бус.
 *
 *   Тиймээс энэ компонент нь БАГАНЫН ТОДОРХОЙЛОЛТЫГ НЬ ДАХИН АШИГЛАНА:
 *   карт доторх утга бүрийг тухайн баганын ӨӨРИЙНХ нь `render`-ээр
 *   гаргана. Үйлдлийн багана ч мөн адил — товчнууд, Popconfirm, Tooltip,
 *   эрхийн шалгалт БҮГД хэвээр, ганц ч мөр хуулбарлагдахгүй.
 *
 * ─────────────────────────────────────────────────────────────────────
 * БАГАНА ТУС БҮРИЙГ ТОХИРУУЛАХ (заавал биш):
 *   mobile: "title"    — картын ГАРЧИГ болно (томоор, дээд талд)
 *   mobile: "actions"  — картын толгойн баруун талд (үйлдлийн товчнууд)
 *   mobile: "badge"    — толгойд шошго маягаар (төлөв, дугаар)
 *   mobile: "hide"     — утсан дээр ОГТ харуулахгүй
 *   (заагаагүй бол «нэр: утга» мөр болно)
 *
 * АВТОМАТ ТААМАГ (`mobile` заагаагүй үед):
 *   • title === "№"        → нуух (картан жагсаалтад дугаар утгагүй)
 *   • title === "Үйлдэл"   → actions
 *   • ЭХНИЙ үлдсэн багана  → title
 */
import React, { useEffect, useState } from "react";
import { Button, Empty, Table } from "antd";

import "src/components/responsiveTable.css";

/** Утга гаргах — баганын өөрийнх нь `render` байвал түүгээр.
 *
 * ⚠ `dataIndex` БАЙХГҮЙ бол эхний аргумент нь БҮТЭН МӨР (2026-09-30).
 *   antd/rc-table яг ингэж ажилладаг: `value = dataIndex ? get(record,
 *   dataIndex) : record`. Энд `undefined` дамжуулж байсан тул antd-ийн
 *   хэв маягаар бичсэн багана бүр — `render: (item) => item.begin_date`
 *   гэх мэт — УТСАН ДЭЭР л унаж, бүх хуудсыг ChunkErrorBoundary болгодог
 *   байв (/me → сургалт: «Cannot read properties of undefined (reading
 *   'begin_date')»). Нэг хуудасны бус, БҮХ хүснэгтэд үйлчлэх алдаа.
 */
function cellOf(col, record, index) {
  const hasIdx = col.dataIndex != null;
  const raw = hasIdx
    ? Array.isArray(col.dataIndex)
      ? col.dataIndex.reduce((a, k) => (a == null ? a : a[k]), record)
      : record[col.dataIndex]
    : record;
  if (typeof col.render === "function") return col.render(raw, record, index);
  // ⚠ `render` ч, `dataIndex` ч байхгүй бол харуулах утга АЛГА. `raw`
  //   (=мөрийн объект) буцаавал React «Objects are not valid as a React
  //   child» гэж унана.
  return hasIdx ? raw : undefined;
}

/** Хоосон эсэх — `null`, `undefined`, `""` бол мөрийг огт гаргахгүй. */
const isEmpty = (v) =>
  v == null || v === "" || (Array.isArray(v) && v.length === 0);

/**
 * Дэлгэц нарийн эсэх.
 *
 * ⚠ `window.innerWidth`-ыг нэг удаа уншаад орхивол утсаа эргүүлэхэд
 *   (portrait ↔ landscape) хуучин утга үлдэнэ — `matchMedia`-гаар сонсоно.
 */
export function useIsNarrow(maxWidth = 820) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(query);
    const on = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    // ⚠ Safari 13 ба доош `addEventListener`-гүй — `addListener` руу унана.
    if (mq.addEventListener) mq.addEventListener("change", on);
    else mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", on);
      else mq.removeListener(on);
    };
  }, [query]);
  return narrow;
}

/** Баганыг үүргээр нь ангилна (автомат таамаг + `mobile` заалт). */
function classify(columns) {
  const cols = (columns || []).filter(Boolean);
  const out = { title: null, actions: [], badges: [], rows: [] };
  let titleTaken = false;
  cols.forEach((c) => {
    const t = typeof c.title === "string" ? c.title.trim() : "";
    let role = c.mobile;
    if (!role) {
      if (t === "№" || t === "Д/д") role = "hide";
      else if (t === "Үйлдэл" || t === "Үйлдлүүд") role = "actions";
      else if (!titleTaken) role = "title";
    }
    if (role === "hide") return;
    if (role === "actions") out.actions.push(c);
    else if (role === "badge") out.badges.push(c);
    else if (role === "title" && !titleTaken) {
      out.title = c;
      titleTaken = true;
    } else out.rows.push(c);
  });
  return out;
}

export default function ResponsiveTable({
  columns,
  dataSource,
  loading,
  pagination,
  rowKey = "id",
  locale,
  /** Нарийн дэлгэцийн хязгаар (px) */
  narrowAt = 820,
  /**
   * Бүлгийн ТОЛГОЙ мөрийг таних функц. Буцаавал картын оронд бүлгийн
   * гарчиг гарна. Жишээ: `(r) => r.__group ? r.bn_name : null`
   */
  groupHeaderOf,
  /** Карт дарахад (заавал биш) */
  onRowClick,
  ...rest
}) {
  const narrow = useIsNarrow(narrowAt);

  if (!narrow) {
    return (
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={pagination}
        rowKey={rowKey}
        locale={locale}
        {...rest}
      />
    );
  }

  const rows = dataSource || [];
  const { title, actions, badges, rows: fieldCols } = classify(columns);

  if (loading) {
    return (
      <div className="rt-state">
        <i className="fas fa-circle-notch fa-spin" /> Ачаалж байна…
      </div>
    );
  }
  if (!rows.length) {
    return (
      locale?.emptyText || <Empty description="Мэдээлэл алга" style={{ padding: 30 }} />
    );
  }

  // Хуудаслалт — antd-ийн `pagination` объектоос уншина (сервер эсвэл клиент).
  const pg = pagination === false ? null : pagination || null;
  const cur = pg?.current || 1;
  const size = pg?.pageSize || rows.length;
  const total = pg?.total != null ? pg.total : rows.length;
  const pages = Math.max(1, Math.ceil(total / (size || 1)));

  const keyOf = (r, i) =>
    typeof rowKey === "function" ? rowKey(r, i) : r?.[rowKey] ?? i;

  return (
    <div className="rt-list">
      {rows.map((r, i) => {
        const head = groupHeaderOf ? groupHeaderOf(r) : null;
        if (head) {
          return (
            <div className="rt-group" key={`g-${i}`}>
              {head}
            </div>
          );
        }
        return (
          <div
            className="rt-card"
            key={keyOf(r, i)}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
          >
            {(badges.length || actions.length) ? (
              <div className="rt-head">
                {badges.map((c, ci) => (
                  <span key={ci}>{cellOf(c, r, i)}</span>
                ))}
                <span className="rt-spacer" />
                {actions.map((c, ci) => (
                  <span key={ci} onClick={(e) => e.stopPropagation()}>
                    {cellOf(c, r, i)}
                  </span>
                ))}
              </div>
            ) : null}

            {title ? <div className="rt-title">{cellOf(title, r, i)}</div> : null}

            {fieldCols.map((c, ci) => {
              const v = cellOf(c, r, i);
              if (isEmpty(v)) return null;
              return (
                <div className="rt-row" key={ci}>
                  {/* ⚠ Баганын толгой утсан дээр ХАРАГДАХГҮЙ тул талбарын
                      НЭРИЙГ мөр бүрд хамт бичнэ — эс бөгөөс «17» гэдэг нь
                      оролцогч уу, минут уу гэдэг нь тодорхойгүй болно. */}
                  <span className="k">
                    {typeof c.title === "string" ? c.title : ""}
                  </span>
                  <span className="v">{v}</span>
                </div>
              );
            })}
          </div>
        );
      })}

      {pages > 1 && pg ? (
        <div className="rt-pager">
          <Button
            size="small"
            disabled={cur <= 1}
            onClick={() => pg.onChange && pg.onChange(cur - 1, size)}
          >
            ← Өмнөх
          </Button>
          <span className="rt-pageinfo">
            {cur} / {pages}
          </span>
          <Button
            size="small"
            disabled={cur >= pages}
            onClick={() => pg.onChange && pg.onChange(cur + 1, size)}
          >
            Дараах →
          </Button>
        </div>
      ) : null}
    </div>
  );
}
